"use client";

import type { StepInputs } from "@fieldtripjs/core";
import { createContext, useContext } from "react";
import type { AddressSuggestion } from "@/features/geo/shared/address-types";
import type { PostalCode } from "@/features/geo/shared/postal-codes";
import type { QuoteFlow, StepId } from "@/features/quote/shared/flow";
import type { Audience } from "@/features/quote/shared/quote-config";

export type StepAnswers<K extends StepId> = Partial<StepInputs<QuoteFlow, K>>;

// Which answers BBR filled in, so a step can say so until the customer changes them.
export type BbrPrefilled = { propertyType: boolean; floor: boolean; sqm: boolean };

// Step components under ./steps/ read the wizard's state and handlers from
// here instead of having 20 props drilled through. The value is built fresh
// on every render in QuoteWizard - it must NOT be memoized, since handlers
// close over the current flow state.
export type WizardContextValue = {
  answersOf: <K extends StepId>(step: K) => StepAnswers<K>;
  answer: <K extends StepId>(step: K, answers: StepAnswers<K>) => void;
  isBusiness: boolean;
  isRecurring: boolean;
  canGoBack: boolean;
  canAdvance: boolean;
  next: () => void;
  back: () => void;
  autoAdvance: (delay?: number) => void;
  selectAudience: (audience: Audience) => void;
  bbrPrefilled: BbrPrefilled;
  clearBbrPrefilled: (field: keyof BbrPrefilled) => void;
  fieldErrors: Record<string, string>;
  setFieldError: (field: string, error: string | null) => void;
  validateEmailField: (value: string) => string | null;
  validatePhoneField: (value: string) => string | null;
  handleAddressSelect: (address: AddressSuggestion) => void;
  handleSkipAddress: () => void;
  handleResetAddress: () => void;
  bbrLoading: boolean;
  bbrError: string | null;
  honeypot: string;
  setHoneypot: (value: string) => void;
  submitError: string | null;
  isSubmitting: boolean;
  handleSubmit: () => void;
  postalMatch: PostalCode | null;
};

const WizardContext = createContext<WizardContextValue | null>(null);

export const WizardProvider = WizardContext.Provider;

export function useWizard(): WizardContextValue {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useWizard skal bruges inden i en WizardProvider");
  return ctx;
}

export function useStep<K extends StepId>(step: K) {
  const { answersOf, answer } = useWizard();
  return [answersOf(step), (answers: StepAnswers<K>) => answer(step, answers)] as const;
}
